import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faXTwitter, faDiscord, faYoutube, faTwitch } from '@fortawesome/free-brands-svg-icons';
import {
  faCertificate,
  faCircleNotch,
  faComment,
  faLink,
  faLock,
  faShareNodes,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { AvatarWithFlag, normalizeCountryCode } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import { AssetUrls } from 'config/assets';
import { useIsMobile } from 'hooks/useIsMobile';
import { SAMPLE_BADGES } from '../FeatureCards/TrophyCaseCard';
import { ChatBubblePair } from '../FeatureCards/ChatCustomizationCard';
import {
  BORDER_PRESETS,
  BUBBLE_PRESETS,
  CHECKOUT_SOURCES,
  FEATURE_CYCLE_MS,
  PROFILE_STUDIO_ID,
  SLUG_ALLOWED_RE,
  SLUG_MAX_LENGTH,
  STUDIO_FEATURE_ORDER,
  StudioFeatureKey,
} from './constants';
import { STUDIO_COPY, STUDIO_FEATURES } from './copy';
import { useReducedMotion } from './useReducedMotion';
import './sections.css';
import './ProfileStudio.css';

const BNG_47_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

const SOCIAL_ICONS: IconProp[] = [faXTwitter as IconProp, faDiscord as IconProp, faYoutube as IconProp, faTwitch as IconProp, faLink as IconProp];

const CHIP_ICONS: Record<StudioFeatureKey, IconProp> = {
  border: faCircleNotch as IconProp,
  chat: faComment as IconProp,
  badge: faCertificate as IconProp,
  url: faLink as IconProp,
  social: faShareNodes as IconProp,
  trophy: faTrophy as IconProp,
};

export interface StudioUser {
  username: string;
  avatarUrl: string | null;
  country: string | null;
  customSlug?: string | null;
  borderColor1?: string | null;
  borderColor2?: string | null;
  bubbleColor1?: string | null;
  bubbleColor2?: string | null;
}

interface ProfileStudioProps {
  feature: StudioFeatureKey;
  onSelectFeature: (feature: StudioFeatureKey) => void;
  /** Once true the auto-cycle stops for good. */
  interacted: boolean;
  onInteract: () => void;
  user: StudioUser | null;
  onSubscribe: (source: string) => void;
  isCheckoutLoading: boolean;
}

type Mode = 'elite' | 'free';

const sanitizeSlug = (raw: string): string => raw.toLowerCase().replace(SLUG_ALLOWED_RE, '').slice(0, SLUG_MAX_LENGTH);

/**
 * ProfileStudio (Asana 1219949663037427, Decisions 4 and 5): a live profile card
 * the visitor flips between Free and Elite, six selectable features with a
 * Try-it panel, all preview-only. Nothing here calls the API.
 */
const ProfileStudio: React.FC<ProfileStudioProps> = ({
  feature,
  onSelectFeature,
  interacted,
  onInteract,
  user,
  onSubscribe,
  isCheckoutLoading,
}) => {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<Mode>('elite');
  const [borderPreset, setBorderPreset] = useState<number>(-1);
  const [bubblePreset, setBubblePreset] = useState<number>(-1);
  const [slug, setSlug] = useState<string>(STUDIO_COPY.loggedOutUsername);

  // Auto-cycle until the first interaction; never under reduced motion.
  useEffect(() => {
    if (interacted || reducedMotion) return;
    const timer = window.setInterval(() => {
      const index = STUDIO_FEATURE_ORDER.indexOf(feature);
      onSelectFeature(STUDIO_FEATURE_ORDER[(index + 1) % STUDIO_FEATURE_ORDER.length]);
    }, FEATURE_CYCLE_MS);
    return () => window.clearInterval(timer);
  }, [feature, interacted, reducedMotion, onSelectFeature]);

  const isElite = mode === 'elite';
  const username = user?.username ?? STUDIO_COPY.loggedOutUsername;
  const [c1, c2] = borderPreset >= 0 ? BORDER_PRESETS[borderPreset] : [user?.borderColor1 || BORDER_PRESETS[0][0], user?.borderColor2 || BORDER_PRESETS[0][1]];
  const [b1, b2] = bubblePreset >= 0 ? BUBBLE_PRESETS[bubblePreset] : [user?.bubbleColor1 || BUBBLE_PRESETS[0][0], user?.bubbleColor2 || BUBBLE_PRESETS[0][1]];
  const freeUrlText = user ? `trademagic.gg/profile/u/${user.username}` : `trademagic.gg/u/${STUDIO_COPY.loggedOutSlug}`;

  const select = (key: StudioFeatureKey) => {
    onInteract();
    onSelectFeature(key);
  };

  const pickBorder = (i: number) => {
    onInteract();
    setBorderPreset(i);
    setMode('elite');
  };

  const pickBubble = (i: number) => {
    onInteract();
    setBubblePreset(i);
    setMode('elite');
  };

  const region = (key: StudioFeatureKey) =>
    `studio-region${feature === key ? ' studio-region--active' : ' studio-region--dim'}`;

  const renderTryIt = () => {
    switch (feature) {
      case 'border':
        return (
          <div className="studio-swatches" role="group" aria-label="Border colors">
            {BORDER_PRESETS.map(([a, b], i) => (
              <button
                key={`${a}-${b}`}
                type="button"
                className={`studio-swatch${borderPreset === i ? ' studio-swatch--active' : ''}`}
                style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
                aria-label={`Border colors ${a} and ${b}`}
                onClick={() => pickBorder(i)}
              />
            ))}
          </div>
        );
      case 'chat':
        return (
          <>
            <div className="studio-swatches" role="group" aria-label="Bubble colors">
              {BUBBLE_PRESETS.map(([a, b], i) => (
                <button
                  key={`${a}-${b}`}
                  type="button"
                  className={`studio-swatch${bubblePreset === i ? ' studio-swatch--active' : ''}`}
                  style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
                  aria-label={`Bubble colors ${a} and ${b}`}
                  onClick={() => pickBubble(i)}
                />
              ))}
            </div>
            <p className="studio-tryit__hint">{STUDIO_COPY.chatHint}</p>
          </>
        );
      case 'url':
        return (
          <label className="studio-url-input">
            <span className="studio-url-input__prefix">trademagic.gg/u/</span>
            <input
              type="text"
              value={slug}
              maxLength={SLUG_MAX_LENGTH}
              aria-label="Custom profile URL"
              onChange={(e) => {
                onInteract();
                setSlug(sanitizeSlug(e.target.value));
              }}
            />
          </label>
        );
      case 'badge':
        return (
          <div className="studio-chips" aria-label="Where the badge shows">
            {STUDIO_COPY.badgeChips.map((chip) => (
              <span key={chip} className="studio-chip">
                <EliteBadge isElite size={14} />
                {chip}
              </span>
            ))}
          </div>
        );
      case 'social':
        return <p className="studio-tryit__text">{STUDIO_COPY.socialTryIt}</p>;
      case 'trophy':
        return <p className="studio-tryit__text">{STUDIO_COPY.trophyTryIt}</p>;
      default:
        return null;
    }
  };

  return (
    <section
      id={PROFILE_STUDIO_ID}
      className="elite-section elite-section--first profile-studio"
      onClickCapture={onInteract}
      data-testid="profile-studio"
    >
      <p className="elite-section__eyebrow">{STUDIO_COPY.eyebrow}</p>
      <h2 className="elite-section__headline">{STUDIO_COPY.headline}</h2>
      <p className="elite-section__sub studio__sub">{STUDIO_COPY.sub}</p>

      <div className="studio__layout">
        <div className="studio__card-column">
          <div className="studio__mode" role="group" aria-label="Profile mode">
            <button
              type="button"
              className={`studio__mode-btn${!isElite ? ' studio__mode-btn--active' : ''}`}
              aria-pressed={!isElite}
              onClick={() => setMode('free')}
            >
              Free
            </button>
            <button
              type="button"
              className={`studio__mode-btn${isElite ? ' studio__mode-btn--active' : ''}`}
              aria-pressed={isElite}
              onClick={() => setMode('elite')}
            >
              Elite
            </button>
            <span className="studio__mode-hint">{isElite ? STUDIO_COPY.modeHint.elite : STUDIO_COPY.modeHint.free}</span>
          </div>

          <div className={`studio-card studio-card--${mode}`} data-testid="studio-card" data-mode={mode}>
            <div
              className="studio-card__banner"
              style={{ backgroundImage: `url(${AssetUrls.cards.getCardImage(BNG_47_SCRYFALL_ID, 'art_crop')})` }}
              aria-hidden="true"
            />
            <div className={`studio-card__avatar ${region('border')}`}>
              <div className={isElite ? '' : 'studio-card__avatar-plain'}>
                <AvatarWithFlag
                  avatarUrl={user?.avatarUrl ?? null}
                  username={username}
                  size={isMobile ? 80 : 112}
                  countryCode={user?.country ? normalizeCountryCode(user.country) : null}
                  isElite={isElite}
                  showAnimatedBorder={isElite}
                  borderColor1={c1}
                  borderColor2={c2}
                  alt=""
                />
              </div>
            </div>
            <div className={`studio-card__name ${region('badge')}`}>
              <span className="studio-card__username">{username}</span>
              <EliteBadge isElite={isElite} size={isMobile ? 24 : 30} />
            </div>
            <div className={`studio-card__url ${region('url')}`}>
              <FontAwesomeIcon icon={faLock} className="studio-card__url-lock" />
              {isElite ? (
                <>
                  <span>trademagic.gg/u/</span>
                  <span className="studio-card__slug">{user?.customSlug || slug}</span>
                </>
              ) : (
                <span className="studio-card__url-plain">{freeUrlText}</span>
              )}
            </div>
            <div className={`studio-card__social ${region('social')}`}>
              {isElite ? (
                SOCIAL_ICONS.map((icon, i) => (
                  <span key={i} className="studio-card__social-icon">
                    <FontAwesomeIcon icon={icon} />
                  </span>
                ))
              ) : (
                <span className="studio-card__social-locked">
                  <FontAwesomeIcon icon={faLock} /> {STUDIO_COPY.socialLocked}
                </span>
              )}
            </div>
            <div className="studio-card__tiles">
              <div className={`studio-card__tile ${region('trophy')}`}>
                <span className="studio-card__tile-label">Trophy Case</span>
                <div className="studio-card__badges">
                  {isElite ? (
                    SAMPLE_BADGES.map((b, i) => (
                      <BadgeIcon key={i} icon={b.icon} shape={b.shape} tier={b.tier} size={isMobile ? 26 : 32} />
                    ))
                  ) : (
                    <>
                      <BadgeIcon icon="trophy" shape="shield" tier="bronze" size={isMobile ? 26 : 32} />
                      <div className="trophy-card-empty-slot" />
                      <div className="trophy-card-empty-slot" />
                      <div className="trophy-card-empty-slot" />
                    </>
                  )}
                </div>
              </div>
              <div className={`studio-card__tile ${region('chat')}`}>
                <span className="studio-card__tile-label">Inbox</span>
                <ChatBubblePair sentBackground={isElite ? `linear-gradient(135deg, ${b1}, ${b2})` : '#3a3a44'} />
              </div>
            </div>
          </div>
        </div>

        <div className="studio__features-column">
          <ul className="studio__list" aria-label="Elite features">
            {STUDIO_FEATURE_ORDER.map((key) => (
              <li key={key}>
                <button
                  type="button"
                  className={`studio__item${feature === key ? ' studio__item--active' : ''}`}
                  aria-pressed={feature === key}
                  onClick={() => select(key)}
                >
                  <span className="studio__item-title">{STUDIO_FEATURES[key].title}</span>
                  <span className="studio__item-desc">{STUDIO_FEATURES[key].description}</span>
                </button>
              </li>
            ))}
          </ul>

          <div className="studio__chips" aria-label="Elite features">
            {STUDIO_FEATURE_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                className={`studio__chip${feature === key ? ' studio__chip--active' : ''}`}
                aria-pressed={feature === key}
                onClick={() => select(key)}
              >
                <FontAwesomeIcon icon={CHIP_ICONS[key]} />
                {STUDIO_FEATURES[key].chip}
              </button>
            ))}
          </div>

          <div className="studio-tryit" data-testid="studio-tryit">
            <h3 className="studio-tryit__title">{STUDIO_FEATURES[feature].title}</h3>
            <p className="studio-tryit__desc">{STUDIO_FEATURES[feature].description}</p>
            {renderTryIt()}
            <button
              type="button"
              className="elite-subscribe-button elite-subscribe-button--shimmer studio-tryit__cta"
              onClick={() => onSubscribe(CHECKOUT_SOURCES.studio)}
              disabled={isCheckoutLoading}
            >
              {isCheckoutLoading ? 'Loading...' : STUDIO_COPY.unlock}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileStudio;
